import { useState, type DragEvent, type ReactNode } from 'react';
import { TASK_STATUS_LABEL, type TaskStatus } from '@todo/shared';

/**
 * A COLLAPSIBLE board column - the "disclosure" pattern applied to Kanban:
 * expanded it is the familiar card column; contracted it folds into a slim
 * vertical rail showing just the chevron, the count and the sideways title,
 * giving the remaining columns the width. One component, reused by every
 * board (Tasks, My Day, Important, Planned, Assigned to/by me).
 *
 * The rail still accepts drops when the column is a valid drop target, so a
 * card can be dragged onto a collapsed Blocked/Completed without expanding
 * it first.
 */

export type BoardColumn = Extract<TaskStatus,'PENDING' | 'ONGOING' | 'BLOCKED' | 'DONE' | 'CANCELLED'>;

/**
 * Each column wears its status: accent top edge, tinted background and a
 * matching border - Not started reads blue at a glance, In progress amber,
 * Blocked red, Completed green, Cancelled neutral.
 */
export const COLUMN_STYLE: Record<
  BoardColumn,
  { top: string; bg: string; border: string }
> = {
  PENDING: { top: 'border-t-sky-400', bg: 'bg-sky-50/60', border: 'border-sky-200' },
  ONGOING: { top: 'border-t-amber-400', bg: 'bg-amber-50/60', border: 'border-amber-200' },
  BLOCKED: { top: 'border-t-red-400', bg: 'bg-red-50/60', border: 'border-red-200' },
  DONE: { top: 'border-t-emerald-400', bg: 'bg-emerald-50/60', border: 'border-emerald-200' },
  CANCELLED: { top: 'border-t-slate-300', bg: 'bg-slate-100/70', border: 'border-slate-200' },
};

/** Every column accepts a drop - "On hold" (stored as CANCELLED) parks a
 *  task and dragging it back out revives it, so it drags like the rest. */
const DROP_TARGETS: readonly BoardColumn[] = ['PENDING', 'ONGOING', 'BLOCKED', 'DONE', 'CANCELLED'];

function Chevron({ left = false }: { left?: boolean }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"
      className={`h-4 w-4 ${left ? '' : 'rotate-180'}`}>
      <path d="M12.5 5L7.5 10l5 5" stroke="currentColor" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function CollapsibleColumn({
  column,
  countLabel,
  onDrop,
  children,
  defaultCollapsed = false,
}: {
  column: BoardColumn;
  /** The header/rail count - a number, or "3 of 7" on paged boards. */
  countLabel: ReactNode;
  /** Present = the column accepts dropped cards (live statuses only). */
  onDrop?: (event: DragEvent, column: BoardColumn) => void;
  children: ReactNode;
  defaultCollapsed?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [dragOver, setDragOver] = useState(false);
  const droppable = !!onDrop && DROP_TARGETS.includes(column);

  const dropHandlers = droppable
    ? {
        onDragOver: (e: DragEvent) => { e.preventDefault(); setDragOver(true); },
        onDragLeave: () => setDragOver(false),
        onDrop: (e: DragEvent) => { setDragOver(false); onDrop?.(e, column); },
      }
    : {};

  const style = COLUMN_STYLE[column];

  if (collapsed) {
    return (
      <section
        {...dropHandlers}
        className={[
          'flex w-11 shrink-0 flex-col items-center rounded-xl border border-t-4 py-2 transition',
          style.top,
          dragOver ? 'border-brand-400 bg-brand-50/60' : `${style.bg} ${style.border}`,
        ].join(' ')}
      >
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-expanded={false}
          aria-label={`Expand ${TASK_STATUS_LABEL[column]}`}
          className="rounded-md p-1 text-slate-400 transition hover:bg-white hover:text-slate-600"
        >
          <Chevron />
        </button>
        <span className="mt-2 text-xs font-semibold tabular-nums text-slate-400">
          {countLabel}
        </span>
        <span className="mt-4 text-sm font-semibold text-slate-600 [writing-mode:vertical-rl]">
          {TASK_STATUS_LABEL[column]}
        </span>
      </section>
    );
  }

  return (
    <section
      {...dropHandlers}
      className={[
        'flex w-72 shrink-0 flex-col rounded-xl border border-t-4 transition',
        style.top,
        dragOver ? 'border-brand-400 bg-brand-50/60' : `${style.bg} ${style.border}`,
      ].join(' ')}
    >
      <header className="flex items-center gap-2 px-4 pb-2 pt-3">
        <h2 className="text-sm font-semibold text-slate-800">{TASK_STATUS_LABEL[column]}</h2>
        <span className="ml-auto text-xs font-medium tabular-nums text-slate-400">
          {countLabel}
        </span>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          aria-expanded
          aria-label={`Collapse ${TASK_STATUS_LABEL[column]}`}
          className="rounded-md p-0.5 text-slate-400 transition hover:bg-white hover:text-slate-600"
        >
          <Chevron left />
        </button>
      </header>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 pb-3">{children}</div>
    </section>
  );
}
